import {
  Text,
  Box,
  HStack,
  VStack,
  Flex,
  Divider,
  Icon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Spinner,
} from "@chakra-ui/react";
import { useEffect } from "react";
import {
  FaCalendarAlt,
  FaFileAlt,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import { format } from "date-fns";
import {
  AnaliseCreditoPasso as AnaliseCreditoPassoType,
  CreditEngineReportResponseData,
  ReportRelatorio,
} from "@/models/consignado/report-data.model";
import { useState } from "react";
import { getCreditEngineProposalReport } from "@/services/consignado/credit-engine-reports.service";
import {
  AnaliseCreditoBlocoCard,
  AnaliseCreditoItem,
  AnaliseCreditoResultado,
  comPassosNaoAplicados,
  NOME_MOTOR_1,
  NOME_MOTOR_BMP,
  passosDoMotor,
  passosDoMotorOriginacao,
  semVazios,
} from "@/components/consignado/AnaliseCreditoPasso";

const TIPO_RELATORIO_RISCO = "Risco";


export function CreditEngineReportProposal({ 
  proposalId,
  fetchReportData = getCreditEngineProposalReport
}: { 
  proposalId: string;
  fetchReportData?: (id: string) => Promise<CreditEngineReportResponseData | null>;
}) {
  const [reportData, setReportData] = useState<CreditEngineReportResponseData | null>(null);

  useEffect(() => {
    fetchReportData(proposalId).then((data) => {
      setReportData(data);
    });
  }, [proposalId, fetchReportData]);

  const formatDateTime = (date: Date | string | null) => {
    if (!date) return "Não informado";
    try {
      const dateObj = typeof date === "string" ? new Date(date) : date;
      return format(dateObj, "dd/MM/yyyy, HH:mm");
    } catch {
      return "Data inválida";
    }
  };

  const handeHasWhitelistedId = (reports?: ReportRelatorio[] ): boolean => {
    if (!reports) return false;
    for (const report of reports){
      if(report.cnpj_pre_aprovado_id){
        return true
      }
    }
    return false
  }

  const analiseCredito = comPassosNaoAplicados(reportData?.analise_credito);

  // Card de um relatório de motor (critérios de análise + passos da
  // analise_credito daquele motor).
  const renderRelatorio = (relatorio: ReportRelatorio) => (
    <Accordion
      key={relatorio.id}
      allowToggle
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
    >
      <AccordionItem border="none">
        <AccordionButton
          p={3}
          _hover={{ bg: "gray.50" }}
          borderRadius="md"
        >
          <Flex flex={1} align="center" justify="space-between">
            <HStack spacing={2}>
              <Box
                bg={relatorio.aprovado ? "green.500" : "red.500"}
                borderRadius="full"
                p={1.5}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon
                  as={
                    relatorio.aprovado
                      ? FaCheckCircle
                      : FaTimesCircle
                  }
                  color="white"
                  boxSize={3}
                />
              </Box>
              <VStack align="start" spacing={0} textAlign="left">

                {relatorio.nome_motor && (
                  <Text fontSize="xs" color="gray.600">
                    <Text as="span" fontWeight="bold">
                      Nome Motor:
                    </Text>{" "}
                    {relatorio.nome_motor}
                  </Text>
                )}
                {relatorio.descricao_motor && (
                  <Text fontSize="xs" color="gray.600">
                    <Text as="span" fontWeight="bold">
                      Descrição Motor:
                    </Text>{" "}
                    {relatorio.descricao_motor}
                  </Text>
                )}

                {relatorio.pontuacao && (
                  <Text fontSize="xs" color="gray.600">
                    Score: {relatorio.pontuacao}
                  </Text>
                )}

                {relatorio.data_criacao && (
                  <HStack spacing={1}>
                  <Icon
                    as={FaCalendarAlt}
                    color="gray.500"
                    boxSize={2.5}
                  />
                    <Text fontSize="xs" color="gray.600">
                      {formatDateTime(relatorio.data_criacao)}
                    </Text>
                  </HStack>
                )}
              </VStack>
            </HStack>
            <AccordionIcon />
          </Flex>
        </AccordionButton>

        <AccordionPanel pb={3} px={3}>
          <Divider mb={3} />
          <Text
            fontSize="xs"
            fontWeight="bold"
            color="gray.700"
            mb={2}
          >
            CRITÉRIOS DE ANÁLISE
          </Text>
          <VStack align="stretch" spacing={2}>
            {relatorio.criterios?.map((criterio) => (
              <Box
                key={criterio.id_criterio}
                p={2}
                bg="gray.50"
                borderRadius="md"
                border="1px solid"
                borderColor="gray.200"
              >
                <HStack spacing={2} mb={1}>
                  <Icon
                    as={
                      criterio.aprovado !== false
                        ? FaCheckCircle
                        : FaTimesCircle
                    }
                    color={
                      criterio.aprovado !== false
                        ? "green.500"
                        : "red.500"
                    }
                    boxSize={3}
                  />
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="gray.700"
                  >
                    {criterio.nome_criterio}
                  </Text>


                  {criterio.mensagem && (
                    <>
                      <Text fontSize="xs" color="gray.600">
                      {" - "}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        {criterio.mensagem}
                      </Text>
                    </>
                  )}
                </HStack>

                {criterio.valor_original !== null &&
                  criterio.valor_original !== undefined && (
                    <Text fontSize="xs" color="gray.500" mt={0.5}>
                      Valor original:{" "}
                      {String(criterio.valor_original)}
                    </Text>
                  )}
              </Box>
            ))}
          </VStack>

          {(() => {
            const passos = passosDoMotor(analiseCredito, relatorio.nome_motor);
            return passos.length > 0 && <AnaliseCreditoResultado passos={passos} mt={4} />;
          })()}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );

  // Mesma montagem da modal de análise da consulta: na ordem de
  // validacoes_cnpj_cpf, o passo de cada motor aparece como o card do motor
  // (quando há relatório dele); os demais, como itens.
  const validacoesCnpjCpf = analiseCredito?.validacoes_cnpj_cpf;
  const relatoriosDoMotor = (nomeMotor: string) =>
    reportData?.relatorios?.filter((relatorio) => relatorio.nome_motor === nomeMotor) ?? [];
  const renderPassoOuMotor = (passo: AnaliseCreditoPassoType | undefined, nomeMotor?: string) => {
    const relatorios = nomeMotor ? relatoriosDoMotor(nomeMotor) : [];
    if (relatorios.length > 0) return relatorios.map(renderRelatorio);
    return passo && <AnaliseCreditoItem passo={passo} />;
  };
  const motoresNasValidacoes = validacoesCnpjCpf ? [NOME_MOTOR_1, NOME_MOTOR_BMP] : [];
  // Os relatórios de risco saem do mesmo enquadramento que gera o passo
  // enquadramento_risco: ficam dentro do card de enquadramento.
  const enquadramento = analiseCredito?.enquadramento;
  const ehRelatorioDeRisco = (relatorio: ReportRelatorio) => relatorio.tipo === TIPO_RELATORIO_RISCO;
  const relatoriosDeRisco = enquadramento ? reportData?.relatorios?.filter(ehRelatorioDeRisco) ?? [] : [];
  const relatoriosForaDosBlocos =
    reportData?.relatorios?.filter(
      (relatorio) =>
        !motoresNasValidacoes.includes(relatorio.nome_motor ?? "") &&
        !(enquadramento && ehRelatorioDeRisco(relatorio))
    ) ?? [];
  const semConteudo = !analiseCredito && (reportData?.relatorios?.length ?? 0) === 0;

  return (
    <Box
      flex={{ base: "none", lg: "1" }}
      bg="white"
      borderRadius="lg"
      p={3}
      boxShadow="sm"
      maxH="500px"
      border="1px solid"
      borderColor="gray.200"
      css={{
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#555",
        },
      }}
      display="flex"
      flexDirection="column"
      overflow="hidden"
      minH={0}
    >
       <Box mb={0} flexShrink={0}>
          <Flex alignItems='center' justifyContent='center' gap={1}>
            <Icon as={FaFileAlt} color="secondary" flexShrink={0} />
            <Text fontSize="md" fontWeight="bold" color="gray.700">
              Relatórios de Análise
            </Text>
          </Flex>

          {reportData?.empregador?.nome && (
            <Text
              fontSize="md"
              fontWeight="bold"
              color="gray.700"
              textAlign="center"
              title={reportData.empregador.nome}
            >
              {reportData.empregador.nome}
            </Text>
          )}
        </Box>

      {reportData ? (
        <>
           {handeHasWhitelistedId(reportData?.relatorios) && (
            <Text ml={5} fontSize='0.6rem' color='green.600' textAlign="left">
              Este CNPJ consta na lista de pré-aprovados.
            </Text>
          )}

          {semConteudo ? (
            <Text color="gray.500" textAlign="center" py={8}>
              Nenhum relatório disponível para este vínculo
            </Text>
          ) : (
            <Box
              flex={1}
              overflowY="auto"
              minH={0}
              mt={3}
              css={{
                overscrollBehavior: "contain",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <VStack align="stretch" spacing={2}>
                {validacoesCnpjCpf && (
                  <AnaliseCreditoBlocoCard
                    titulo="Validações de CNPJ e CPF"
                    descricao="valida empregador e trabalhador: critérios mínimos, aprovação do CNPJ, quantidade de funcionários, margem na bancarizadora e critérios totais"
                    passos={semVazios(Object.values(validacoesCnpjCpf))}
                  >
                    <VStack align="stretch" spacing={2}>
                      {renderPassoOuMotor(validacoesCnpjCpf.criterios_dados_minimos, NOME_MOTOR_1)}
                      {renderPassoOuMotor(validacoesCnpjCpf.aprovacao_pj)}
                      {renderPassoOuMotor(validacoesCnpjCpf.quantidade_funcionarios)}
                      {renderPassoOuMotor(validacoesCnpjCpf.consulta_margem)}
                      {renderPassoOuMotor(validacoesCnpjCpf.criterios_dados_totais, NOME_MOTOR_BMP)}
                    </VStack>
                  </AnaliseCreditoBlocoCard>
                )}

                {relatoriosForaDosBlocos.map(renderRelatorio)}

                {analiseCredito?.motor_originacao && (
                  <AnaliseCreditoBlocoCard
                    titulo="Motor de Originação"
                    descricao="analisa o score, a dívida de FGTS do CNPJ e a situação do CPF e do CNPJ na Receita"
                    passos={passosDoMotorOriginacao(analiseCredito.motor_originacao)}
                    rotuloDescricaoMotor
                  />
                )}

                {enquadramento && (
                  <AnaliseCreditoBlocoCard
                    titulo="Enquadramento de Risco e Produtos"
                    descricao="Apresenta a classificação de risco e os produtos consignados elegíveis."
                    passos={semVazios(Object.values(enquadramento))}
                    rotuloDescricaoMotor
                  >
                    {relatoriosDeRisco.length > 0 && (
                      <VStack align="stretch" spacing={2} mb={2}>
                        {relatoriosDeRisco.map(renderRelatorio)}
                      </VStack>
                    )}
                    <AnaliseCreditoResultado
                      passos={semVazios([enquadramento.enquadramento_risco, enquadramento.produtos_validos])}
                    />
                  </AnaliseCreditoBlocoCard>
                )}
              </VStack>
            </Box>
          )}
        </>
      ) : (
        <Flex justifyContent="center" alignItems="center" height="100%" flexDirection="column" gap={4}>
          <Spinner size="xl" color="primary" thickness="4px" speed="0.65s" />
          <Text fontSize="xl" fontWeight="medium">
            Carregando...
          </Text>
        </Flex>
      )}
    </Box>
  );
}
